import { useEffect, useReducer, useRef, useState } from 'react'
import {
    getLockHeight,
    initialKeyboardLockState,
    isEditableElement,
    isKeyboardLikelyOpen,
    reduceKeyboardLock,
} from '../utils/viewport/keyboardLock'

const LOCK_HEIGHT_VAR = '--app-lock-height'

export interface UseKeyboardViewportLockResult {
    /** px currently forced on the app root; null — no lock. */
    lockHeight: number | null
    /** isKeyboardLikelyOpen(stableHeight, currentHeight) while the lock is active. */
    isKeyboardOpen: boolean
}

const isCoarsePointer = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches

/**
 * Keeps the app root at the full (keyboard-less) viewport height while an editable field is focused
 * on touch devices, so WKWebView-style keyboards (iOS Chrome / PWA) don't squash the layout.
 * Side effects: `data-vk-lock` + `--app-lock-height` on <html>; when the lock is released, <body> scroll is
 * reset and `scrollTo(0, 0)` undoes any document pan.
 */
export function useKeyboardViewportLock(): UseKeyboardViewportLockResult {
    const [state, dispatch] = useReducer(reduceKeyboardLock, initialKeyboardLockState)
    const [currentHeight, setCurrentHeight] = useState(() => window.innerHeight)
    const [coarsePointer] = useState(isCoarsePointer)
    const wasLockedRef = useRef(false)

    useEffect(() => {
        let settleTimer: ReturnType<typeof setTimeout> | undefined

        const readSize = () => {
            setCurrentHeight(window.innerHeight)
            dispatch({ type: 'resize', width: window.innerWidth, height: window.innerHeight })
        }

        const onFocusIn = (event: FocusEvent) => {
            dispatch({ type: 'focusin', editable: isEditableElement(event.target as Element | null) })
        }

        // focusout fires before focusin of the next field — decide after a tick by the real activeElement.
        const onFocusOut = () => {
            clearTimeout(settleTimer)
            settleTimer = setTimeout(() => {
                settleTimer = undefined
                dispatch({ type: 'focusout-settled', editable: isEditableElement(document.activeElement) })
            }, 0)
        }

        readSize()
        const visualViewport = window.visualViewport
        window.addEventListener('resize', readSize)
        window.addEventListener('orientationchange', readSize)
        visualViewport?.addEventListener('resize', readSize)
        document.addEventListener('focusin', onFocusIn)
        document.addEventListener('focusout', onFocusOut)

        return () => {
            window.removeEventListener('resize', readSize)
            window.removeEventListener('orientationchange', readSize)
            visualViewport?.removeEventListener('resize', readSize)
            document.removeEventListener('focusin', onFocusIn)
            document.removeEventListener('focusout', onFocusOut)
            clearTimeout(settleTimer)
        }
    }, [])

    const lockHeight = getLockHeight(state, coarsePointer)

    useEffect(() => {
        const rootEl = document.documentElement
        if (lockHeight !== null) {
            rootEl.dataset.vkLock = '1'
            rootEl.style.setProperty(LOCK_HEIGHT_VAR, `${lockHeight}px`)
            wasLockedRef.current = true
            return
        }
        // While locked the overflow scrolls inside <body> (index.css) — reset it before it stops scrolling.
        if (wasLockedRef.current && document.body.scrollTop > 0) document.body.scrollTop = 0
        delete rootEl.dataset.vkLock
        rootEl.style.removeProperty(LOCK_HEIGHT_VAR)
        // iOS may leave the document panned after the keyboard closes.
        if (wasLockedRef.current && window.scrollY > 0) window.scrollTo(0, 0)
        wasLockedRef.current = false
    }, [lockHeight])

    useEffect(
        () => () => {
            const rootEl = document.documentElement
            delete rootEl.dataset.vkLock
            rootEl.style.removeProperty(LOCK_HEIGHT_VAR)
        },
        [],
    )

    return {
        lockHeight,
        isKeyboardOpen: lockHeight !== null && isKeyboardLikelyOpen(state.stableHeight, currentHeight),
    }
}
